import type * as Y from 'yjs';
import { ydocMaps } from './schema';
import type { CanvasDoc, Frame, ThemeToken, Tour, CommentThread } from '../types';

/** Apply minimal set/delete ops to bring `ymap` from prev → next, keyed by id.
 *  Equality is reference-based: applyCommand structurally shares unchanged items,
 *  so a ref-equal value means "unchanged" → no write. */
function diffKeyedCollection<T>(ymap: Y.Map<T>, prev: T[], next: T[], idOf: (t: T) => string): void {
  const prevById = new Map(prev.map((x) => [idOf(x), x]));
  const nextById = new Map(next.map((x) => [idOf(x), x]));
  for (const [id, val] of nextById) {
    const before = prevById.get(id);
    if (before !== val) ymap.set(id, val);
  }
  for (const id of prevById.keys()) {
    if (!nextById.has(id)) ymap.delete(id);
  }
}

/** Diff a reducer's doc output into the Y.Doc collections. MUST run inside a
 *  ydoc.transact(..., LOCAL_ORIGIN). Viewport is intentionally ignored (local). */
export function syncToY(ydoc: Y.Doc, prev: CanvasDoc, next: CanvasDoc): void {
  const m = ydocMaps(ydoc);
  diffKeyedCollection<Frame>(m.frames, prev.frames, next.frames, (f) => f.id);
  diffKeyedCollection<CommentThread>(m.comments, prev.comments ?? [], next.comments ?? [], (c) => c.id);
  diffKeyedCollection<ThemeToken>(m.themes, prev.themes ?? [], next.themes ?? [], (t) => t.id);
  diffKeyedCollection<Tour>(m.tours, prev.tours ?? [], next.tours ?? [], (t) => t.id);
  if (prev.activeThemeId !== next.activeThemeId) {
    if (next.activeThemeId === undefined) m.scalars.delete('activeThemeId');
    else m.scalars.set('activeThemeId', next.activeThemeId);
  }
  if (prev.cfIdScheme !== next.cfIdScheme) {
    if (next.cfIdScheme === undefined) m.scalars.delete('cfIdScheme');
    else m.scalars.set('cfIdScheme', next.cfIdScheme);
  }
}
